import { FC, useMemo } from 'react'
import { Controller, FieldArrayPath, useFormContext } from 'react-hook-form'

import { Security } from 'common/security/security'
import { MultiSelectWithSelectedOnTop } from 'modules/Monitoring/components/MultiSelectWithSelectedOnTop/MultiSelectWithSelectedOnTop'
import {
  AlertingPolicyCreateRequest,
  SlackUserGroup,
  User,
} from 'modules/Monitoring/types'

import { SLACK_GROUP_PREFIX, SLACK_USER_PREFIX } from '../utils'

type QueueHealthPolicyThresholdSlackUserGroupsProps = {
  index: number
  fieldName: FieldArrayPath<AlertingPolicyCreateRequest>
  userGroups?: SlackUserGroup[]
  users?: User[]
  isPending: boolean
}

export const QueueHealthPolicyThresholdSlackUserGroups: FC<
  QueueHealthPolicyThresholdSlackUserGroupsProps
> = ({ fieldName, index, userGroups, users, isPending }) => {
  const { control } = useFormContext()

  const isReadonly = !Security.canCreatePolicy

  const slackGroupOrUserOptions = useMemo(() => {
    const slackGroupOptions =
      userGroups?.map(({ slackUserGroupId, name }) => ({
        key: slackUserGroupId,
        value: `${SLACK_GROUP_PREFIX}${slackUserGroupId}`,
        label: `@${name}`,
      })) ?? []
    const slackUsersOptions =
      users
        ?.filter(({ slackUserId }) => Boolean(slackUserId))
        .map(({ userId, lastName, firstName }) => ({
          key: userId,
          value: `${SLACK_USER_PREFIX}${userId}`,
          label: `${firstName} ${lastName}`,
        })) ?? []

    return [...slackGroupOptions, ...slackUsersOptions]
  }, [userGroups, users])

  return (
    <Controller
      name={`${fieldName}.${index}.slackUsersAndGroups`}
      control={control}
      rules={{
        required: 'Field is required',
      }}
      render={({ field, fieldState }) => (
        <MultiSelectWithSelectedOnTop
          options={slackGroupOrUserOptions}
          label="Notify"
          labelList="Notify"
          invalid={!!fieldState.error}
          errorMessage={fieldState.error?.message}
          disabled={isReadonly}
          pending={isPending}
          valueAsTags
          {...field}
        />
      )}
    />
  )
}
